import React, { useState } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,

  Avatar,
  Menu,
  MenuItem,
  Badge,
  Chip,
  Paper,
} from '@mui/material';
import { notificationService } from '../../services/api/notificationService';
import {
  Menu as MenuIcon,
  Dashboard,
  Assignment,
  Description,
  People,
  Settings,
  Logout,
  Notifications,
  AccountCircle,
  School,
  Search,
  Verified,
  Category,
  CloudUpload,
  Analytics,
  Folder,
  CheckCircleOutline,
  Add,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { signingService } from '../../services/api/signingService';

const drawerWidth = 280;

interface DashboardLayoutProps {
  children: React.ReactNode;
}

interface MenuItemType {
  text: string;
  icon: React.ReactElement;
  path: string;
  description: string;
  badge?: number;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState<null | HTMLElement>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleNotificationClick = async (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchorEl(event.currentTarget);

    // Fetch notifications when opening dropdown
    setLoadingNotifications(true);
    try {
      const response = await notificationService.getNotifications(1, 5);
      console.log('[DashboardLayout] Notifications response:', response);
      setNotifications(response?.notifications || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setNotifications([]);
    } finally {
      setLoadingNotifications(false);
    }
  };

  const handleNotificationClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleViewAllNotifications = () => {
    handleNotificationClose();
    // Navigate to correct path based on user role
    const role = typeof user?.role === 'string' ? user?.role : user?.role?.name?.toLowerCase();
    if (role === 'signer') {
      navigate('/signer/notifications');
    } else {
      navigate('/notifications');
    }
  };

  // Fetch notification count
  React.useEffect(() => {
    const fetchNotificationCount = async () => {
      try {
        const response = await notificationService.getUnreadCount();
        console.log('[DashboardLayout] Unread count response:', response);
        setUnreadCount(response?.count || 0);
      } catch (error) {
        console.error('Error fetching notification count:', error);
        setUnreadCount(0);
      }
    };

    if (user) {
      fetchNotificationCount();
      // Poll for new notifications every 30 seconds
      const interval = setInterval(fetchNotificationCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);


  const handleProfile = () => {
    navigate('/profile');
    handleClose();
  };

  const handleSettings = () => {
    navigate('/settings');
    handleClose();
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    handleClose();
  };

  // Get user role name (support both string and object format)
  const userRole = typeof user?.role === 'string' ? user?.role : user?.role?.name?.toLowerCase();

  const getRoleInfo = () => {
    switch (userRole) {
      case 'student':
        return {
          title: 'นักศึกษา',
          subtitle: 'Student Portal',
          bgColor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          textColor: '#ffffff',
          icon: '🎓'
        };
      case 'staff':
        return {
          title: 'เจ้าหน้าที่',
          subtitle: 'Staff Portal',
          bgColor: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          textColor: '#ffffff',
          icon: '👨‍💼'
        };
      case 'signer':
        return {
          title: 'ผู้บริหาร',
          subtitle: 'Signer Portal',
          bgColor: 'linear-gradient(135deg, #d32f2f 0%, #f44336 100%)',
          textColor: '#ffffff',
          icon: '✍️'
        };
      case 'admin':
        return {
          title: 'ผู้ดูแลระบบ',
          subtitle: 'Admin Portal',
          bgColor: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
          textColor: '#ffffff',
          icon: '⚙️'
        };
      default:
        return {
          title: 'ผู้ใช้งาน',
          subtitle: 'User Portal',
          bgColor: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
          textColor: '#333333',
          icon: '👤'
        };
    }
  };

  const getMenuItems = (): MenuItemType[] => {
    const baseItems = [
      {
        text: 'หน้าแรก',
        icon: <Dashboard />,
        path: '/dashboard',
        description: 'ภาพรวมและสถานะ'
      },
    ];

    if (userRole === 'student') {
      return [
        ...baseItems,
        {
          text: 'กิจกรรมที่เข้าร่วม',
          icon: <School />,
          path: '/student/activities',
          description: 'ดูกิจกรรมที่เข้าร่วมทั้งหมด'
        },
        {
          text: 'เกียรติบัตรของฉัน',
          icon: <Assignment />,
          path: '/certificates',
          description: 'เกียรติบัตรที่ได้รับ',
          badge: 3
        },
        {
          text: 'ค้นหาขั้นสูง',
          icon: <Search />,
          path: '/search',
          description: 'ค้นหาเกียรติบัตรและข้อมูล'
        },
        {
          text: 'ตรวจสอบเกียรติบัตร',
          icon: <Verified />,
          path: '/verify',
          description: 'ตรวจสอบความถูกต้อง'
        },
      ];
    }

    if (userRole === 'staff') {
      return [
        ...baseItems,
        {
          text: 'จัดการกิจกรรม',
          icon: <School />,
          path: '/activities',
          description: 'จัดการกิจกรรมทั้งหมด'
        },
        {
          text: 'สร้างกิจกรรม',
          icon: <Add />,
          path: '/activities/create',
          description: 'สร้างกิจกรรมใหม่'
        },
        {
          text: 'สร้างเกียรติบัตร',
          icon: <Assignment />,
          path: '/certificates/generate',
          description: 'สร้างเกียรติบัตรอัตโนมัติ'
        },
        {
          text: 'จัดการเกียรติบัตร',
          icon: <Assignment />,
          path: '/certificates/staff',
          description: 'จัดการเกียรติบัตรตามกิจกรรม'
        },
        {
          text: 'แม่แบบเกียรติบัตร',
          icon: <Description />,
          path: '/templates',
          description: 'จัดการเทมเพลต'
        },
        {
          text: 'ออกแบบเกียรติบัตร',
          icon: <Category />,
          path: '/template-designer',
          description: 'ออกแบบเกียรติบัตรด้วย Designer'
        },
        // HIDDEN: Bulk Operations - Use /certificates/generate instead
        // {
        //   text: 'การดำเนินการแบบกลุ่ม',
        //   icon: <CloudUpload />,
        //   path: '/bulk-operations',
        //   description: 'นำเข้าและสร้างเกียรติบัตรจำนวนมาก'
        // },
        {
          text: 'รายงานและสถิติ',
          icon: <Analytics />,
          path: '/analytics',
          description: 'ดูสถิติและสร้างรายงาน'
        },
        {
          text: 'ค้นหาขั้นสูง',
          icon: <Search />,
          path: '/search',
          description: 'ค้นหาเกียรติบัตรและข้อมูล'
        },
        {
          text: 'จัดการไฟล์',
          icon: <Folder />,
          path: '/files',
          description: 'จัดการไฟล์และรูปภาพ'
        },
        {
          text: 'จัดการนักศึกษา',
          icon: <People />,
          path: '/students',
          description: 'ข้อมูลนักศึกษา'
        },
      ];
    }

    if (userRole === 'signer') {
      return [
        ...baseItems,
        {
          text: 'เอกสารรอลงนาม',
          icon: <Assignment />,
          path: '/signer/pending',
          description: 'เอกสารที่รอการลงนาม'
        },
        {
          text: 'เอกสารที่ลงนามแล้ว',
          icon: <CheckCircleOutline />,
          path: '/signer/completed',
          description: 'เอกสารที่ลงนามเสร็จแล้ว'
        },
        {
          text: 'ลายเซ็นของฉัน',
          icon: <Description />,
          path: '/signer/signatures',
          description: 'จัดการลายเซ็นดิจิทัล'
        },
        {
          text: 'ประวัติการลงนาม',
          icon: <Analytics />,
          path: '/signer/history',
          description: 'ประวัติการลงนามทั้งหมด'
        },
        {
          text: 'รายงานประจำเดือน',
          icon: <Analytics />,
          path: '/signer/reports/monthly',
          description: 'รายงานสถิติรายเดือน'
        },
        {
          text: 'สถิติและรายงาน',
          icon: <Analytics />,
          path: '/signer/analytics',
          description: 'วิเคราะห์ประสิทธิภาพ'
        },
      ];
    }

    if (userRole === 'admin') {
      return [
        ...baseItems,
        {
          text: 'อนุมัติเกียรติบัตร',
          icon: <CheckCircleOutline />,
          path: '/certificates/approver',
          description: 'พิจารณาอนุมัติเกียรติบัตร',
          badge: 5
        },
        {
          text: 'จัดการกิจกรรม',
          icon: <School />,
          path: '/activities',
          description: 'จัดการกิจกรรมทั้งหมด'
        },
        {
          text: 'สร้างกิจกรรม',
          icon: <Add />,
          path: '/activities/create',
          description: 'สร้างกิจกรรมใหม่'
        },
        {
          text: 'สร้างเกียรติบัตร',
          icon: <Assignment />,
          path: '/certificates/generate',
          description: 'สร้างเกียรติบัตรอัตโนมัติ'
        },
        {
          text: 'จัดการเกียรติบัตร',
          icon: <Assignment />,
          path: '/certificates/staff',
          description: 'จัดการเกียรติบัตรตามกิจกรรม'
        },
        {
          text: 'แม่แบบเกียรติบัตร',
          icon: <Description />,
          path: '/templates',
          description: 'จัดการเทมเพลต'
        },
        {
          text: 'ออกแบบเกียรติบัตร',
          icon: <Category />,
          path: '/template-designer',
          description: 'ออกแบบเกียรติบัตรด้วย Designer'
        },
        // HIDDEN: Bulk Operations - Use /certificates/generate instead
        // {
        //   text: 'การดำเนินการแบบกลุ่ม',
        //   icon: <CloudUpload />,
        //   path: '/bulk-operations',
        //   description: 'นำเข้าและสร้างเกียรติบัตรจำนวนมาก'
        // },
        { 
          text: 'รายงานและสถิติ', 
          icon: <Analytics />, 
          path: '/analytics',
          description: 'ดูสถิติและสร้างรายงาน'
        },
        { 
          text: 'ค้นหาขั้นสูง', 
          icon: <Search />, 
          path: '/search',
          description: 'ค้นหาเกียรติบัตรและข้อมูล'
        },
        { 
          text: 'จัดการไฟล์', 
          icon: <Folder />, 
          path: '/files',
          description: 'จัดการไฟล์และรูปภาพ'
        },
        { 
          text: 'จัดการผู้ใช้', 
          icon: <People />, 
          path: '/users',
          description: 'จัดการบัญชีผู้ใช้'
        },
        { 
          text: 'ตั้งค่าระบบ', 
          icon: <Settings />, 
          path: '/settings',
          description: 'การตั้งค่าทั่วไป'
        },
      ];
    }

    return baseItems;
  };

  const roleInfo = getRoleInfo();

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Role Header */}
      <Box
        sx={{
          background: roleInfo.bgColor,
          color: roleInfo.textColor,
          p: 3,
          borderBottom: '1px solid #e0e0e0'
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '12px',
              background: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px'
            }}
          >
            {roleInfo.icon}
          </Box>
          <Box>
            <Typography variant="h6" fontWeight="bold" sx={{ fontFamily: 'Sarabun, sans-serif' }}>
              {roleInfo.title}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9, fontFamily: 'Inter, sans-serif' }}>
              {roleInfo.subtitle}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Navigation Menu */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        <List sx={{ gap: 1 }}>
          {getMenuItems().map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <ListItem key={item.text} disablePadding sx={{ mb: 1 }}>
                <ListItemButton
                  onClick={() => navigate(item.path)}
                  sx={{
                    borderRadius: '12px',
                    px: 2,
                    py: 1.5,
                    backgroundColor: isActive ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                    border: isActive ? '2px solid #1976d2' : '2px solid transparent',
                    '&:hover': {
                      backgroundColor: isActive ? 'rgba(25, 118, 210, 0.12)' : 'rgba(0, 0, 0, 0.04)',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                    },
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: isActive ? '#1976d2' : '#666',
                      minWidth: 40
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body2"
                      fontWeight={isActive ? 600 : 500}
                      sx={{
                        color: isActive ? '#1976d2' : '#333',
                        fontFamily: 'Sarabun, sans-serif'
                      }}
                    >
                      {item.text}
                    </Typography>
                    {item.description && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: isActive ? '#1976d2' : '#666',
                          opacity: 0.8,
                          fontFamily: 'Inter, sans-serif',
                          fontSize: '0.75rem'
                        }}
                      >
                        {item.description}
                      </Typography>
                    )}
                  </Box>
                  {item.badge && (
                    <Chip
                      label={item.badge}
                      size="small"
                      sx={{
                        backgroundColor: isActive ? '#1976d2' : '#f44336',
                        color: 'white',
                        fontSize: '0.75rem',
                        height: '20px',
                        minWidth: '20px'
                      }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>

      {/* Public Verification Link */}
      <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
        <ListItemButton
          onClick={() => navigate('/verify')}
          sx={{
            borderRadius: '12px',
            backgroundColor: 'rgba(76, 175, 80, 0.08)',
            border: '1px solid rgba(76, 175, 80, 0.2)',
            mb: 2,
            '&:hover': {
              backgroundColor: 'rgba(76, 175, 80, 0.12)',
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 8px rgba(76, 175, 80, 0.2)'
            },
            transition: 'all 0.2s ease-in-out'
          }}
        >
          <ListItemIcon sx={{ color: '#4caf50', minWidth: 40 }}>
            <Verified />
          </ListItemIcon>
          <Box>
            <Typography variant="body2" fontWeight="600" sx={{ color: '#4caf50', fontFamily: 'Sarabun, sans-serif' }}>
              ตรวจสอบเกียรติบัตร
            </Typography>
            <Typography variant="caption" sx={{ color: '#4caf50', opacity: 0.8, fontFamily: 'Inter, sans-serif' }}>
              สำหรับบุคคลทั่วไป
            </Typography>
          </Box>
        </ListItemButton>

        <Paper
          sx={{
            p: 2,
            backgroundColor: '#f5f5f5',
            borderRadius: '12px',
            textAlign: 'center'
          }}
        >
          <Typography variant="body2" fontWeight="600" sx={{ fontFamily: 'Sarabun, sans-serif', mb: 0.5 }}>
            ต้องการความช่วยเหลือ?
          </Typography>
          <Typography variant="caption" sx={{ color: '#666', fontFamily: 'Inter, sans-serif' }}>
            กด F1 หรือติดต่อ IT Support
          </Typography>
        </Paper>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          backgroundColor: 'white',
          borderBottom: '1px solid #e0e0e0',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}
      >
        <Toolbar sx={{ minHeight: '64px !important' }}>
          <IconButton
            color="primary"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ 
              mr: 2, 
              display: { sm: 'none' },
              backgroundColor: 'rgba(25, 118, 210, 0.08)',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.12)'
              }
            }}
          >
            <MenuIcon />
          </IconButton>
          
          {/* Logo and Title */}
          <Box display="flex" alignItems="center" sx={{ flexGrow: 1 }}>
            <School sx={{ color: '#1976d2', fontSize: 32, mr: 2 }} />
            <Box>
              <Typography 
                variant="h6" 
                noWrap 
                component="div" 
                sx={{ 
                  color: '#1976d2',
                  fontWeight: 'bold',
                  fontFamily: 'Sarabun, sans-serif',
                  fontSize: '1.25rem'
                }}
              >
                ระบบเกียรติบัตรออนไลน์
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: '#666',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.75rem'
                }}
              >
                คณะเศรษฐศาสตร์ มหาวิทยาลัยธรรมศาสตร์
              </Typography>
            </Box>
          </Box>
          
          {/* Right side actions */}
          <Box display="flex" alignItems="center" gap={1}>
            {/* Notifications */}
            <IconButton
              onClick={handleNotificationClick}
              sx={{
                color: '#666',
                backgroundColor: 'rgba(0,0,0,0.04)',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.08)'
                }
              }}
            >
              <Badge badgeContent={unreadCount} color="error">
                <Notifications />
              </Badge>
            </IconButton>

            {/* Notification Menu */}
            <Menu
              anchorEl={notificationAnchorEl}
              open={Boolean(notificationAnchorEl)}
              onClose={handleNotificationClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              PaperProps={{
                sx: {
                  mt: 1,
                  borderRadius: '12px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  border: '1px solid #e0e0e0',
                  minWidth: 360,
                  maxWidth: 400
                }
              }}
            >
              {/* Notification Header */}
              <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
                <Typography variant="h6" fontWeight="bold" sx={{ fontFamily: 'Sarabun, sans-serif' }}>
                  การแจ้งเตือน
                </Typography>
                <Typography variant="caption" sx={{ color: '#666', fontFamily: 'Inter, sans-serif' }}>
                  คุณมี {unreadCount} การแจ้งเตือนที่ยังไม่ได้อ่าน
                </Typography>
              </Box>

              {/* Notification List */}
              <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                {loadingNotifications ? (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ color: '#666', fontFamily: 'Sarabun, sans-serif' }}>
                      กำลังโหลด...
                    </Typography>
                  </Box>
                ) : notifications.length === 0 ? (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Notifications sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
                    <Typography variant="body2" sx={{ color: '#999', fontFamily: 'Sarabun, sans-serif' }}>
                      ไม่มีการแจ้งเตือนใหม่
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    {notifications.map((notification) => (
                      <MenuItem
                        key={notification.id}
                        onClick={() => {
                          if (notification.link) {
                            navigate(notification.link);
                            handleNotificationClose();
                          }
                        }}
                        sx={{
                          py: 2,
                          px: 3,
                          borderBottom: '1px solid #f0f0f0',
                          backgroundColor: notification.is_read ? 'transparent' : 'rgba(25, 118, 210, 0.04)',
                          '&:hover': {
                            backgroundColor: notification.is_read ? 'rgba(0, 0, 0, 0.04)' : 'rgba(25, 118, 210, 0.08)'
                          }
                        }}
                      >
                        <Box sx={{ width: '100%' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 0.5 }}>
                            <Typography
                              variant="body2"
                              fontWeight={notification.is_read ? 'normal' : 'bold'}
                              sx={{ fontFamily: 'Sarabun, sans-serif', flex: 1 }}
                            >
                              {notification.title}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              {notification.priority === 'urgent' && (
                                <Chip
                                  label="ด่วน"
                                  size="small"
                                  color="error"
                                  sx={{
                                    height: 18,
                                    fontSize: '0.65rem'
                                  }}
                                />
                              )}
                              {notification.priority === 'high' && (
                                <Chip
                                  label="สำคัญ"
                                  size="small"
                                  color="warning"
                                  sx={{
                                    height: 18,
                                    fontSize: '0.65rem'
                                  }}
                                />
                              )}
                              {!notification.is_read && (
                                <Chip
                                  label="ใหม่"
                                  size="small"
                                  color="primary"
                                  sx={{
                                    height: 18,
                                    fontSize: '0.65rem'
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#666',
                              fontFamily: 'Sarabun, sans-serif',
                              display: 'block',
                              mb: 0.5
                            }}
                          >
                            {notification.message}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#999',
                              fontFamily: 'Inter, sans-serif',
                              fontSize: '0.7rem'
                            }}
                          >
                            {new Date(notification.created_at).toLocaleDateString('th-TH', {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Box>
                )}
              </Box>

              {/* View All Button */}
              <Box sx={{ p: 1.5, borderTop: '1px solid #e0e0e0' }}>
                <MenuItem
                  onClick={handleViewAllNotifications}
                  sx={{
                    borderRadius: '8px',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    color: '#1976d2',
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.04)'
                    }
                  }}
                >
                  <Typography sx={{ fontFamily: 'Sarabun, sans-serif' }}>
                    ดูการแจ้งเตือนทั้งหมด
                  </Typography>
                </MenuItem>
              </Box>
            </Menu>

            {/* User Menu */}
            <Box sx={{ position: 'relative' }}>
              <IconButton
                onClick={handleClick}
                sx={{
                  p: 0.5,
                  border: '2px solid transparent',
                  '&:hover': {
                    border: '2px solid #1976d2'
                  }
                }}
              >
                <Avatar 
                  sx={{ 
                    width: 40, 
                    height: 40,
                    backgroundColor: roleInfo.bgColor.includes('gradient') ? '#1976d2' : roleInfo.bgColor,
                    fontSize: '1rem',
                    fontWeight: 'bold'
                  }}
                >
                  {user?.profile?.firstName?.charAt(0) || user?.username?.charAt(0) || 'U'}
                </Avatar>
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                PaperProps={{
                  sx: {
                    mt: 1,
                    borderRadius: '12px',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    border: '1px solid #e0e0e0',
                    minWidth: 200
                  }
                }}
              >
                {/* User Info Header */}
                <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ fontFamily: 'Sarabun, sans-serif' }}>
                    {user?.profile?.firstName || user?.username || 'ผู้ใช้งาน'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#666', fontFamily: 'Inter, sans-serif' }}>
                    {user?.email || 'user@example.com'}
                  </Typography>
                  <Chip 
                    label={roleInfo.title} 
                    size="small" 
                    sx={{ 
                      mt: 1,
                      backgroundColor: 'rgba(25, 118, 210, 0.08)',
                      color: '#1976d2',
                      fontSize: '0.75rem'
                    }} 
                  />
                </Box>
                
                <MenuItem 
                  onClick={handleProfile}
                  sx={{
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.04)'
                    }
                  }}
                >
                  <AccountCircle sx={{ mr: 2, color: '#666' }} />
                  <Typography sx={{ fontFamily: 'Sarabun, sans-serif' }}>ข้อมูลส่วนตัว</Typography>
                </MenuItem>
                <MenuItem 
                  onClick={handleSettings}
                  sx={{
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.04)'
                    }
                  }}
                >
                  <Settings sx={{ mr: 2, color: '#666' }} />
                  <Typography sx={{ fontFamily: 'Sarabun, sans-serif' }}>ตั้งค่า</Typography>
                </MenuItem>
                <Divider sx={{ my: 1 }} />
                <MenuItem 
                  onClick={handleLogout}
                  sx={{
                    py: 1.5,
                    color: '#f44336',
                    '&:hover': {
                      backgroundColor: 'rgba(244, 67, 54, 0.04)'
                    }
                  }}
                >
                  <Logout sx={{ mr: 2 }} />
                  <Typography sx={{ fontFamily: 'Sarabun, sans-serif' }}>ออกจากระบบ</Typography>
                </MenuItem>
              </Menu>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>
      
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default DashboardLayout;
